/**
 * Print Model Tab Configuration
 * Extracted from Figma mockups: InventoZen Print Module
 *
 * Each print model has a specific set of tabs available in the print configuration drawer.
 * This configuration determines which tabs are displayed based on the selected print model.
 */

import { visibleT } from "@/lib/cms/cms-new-i18n";

export enum PrintModel {
  SIMPLE = 'simple',
  COMPLET = 'complet',
  CLASSEUR = 'classeur',
  LIST_TEXT = 'list-text',
}

export enum PrintTab {
  // Universal tabs (ALL models)
  OPTIONS_AFFICHAGE = 'options_affichage',
  PREVISUALISATION = 'previsualisation',

  // Content tabs (Simple, Complet)
  IMAGES_SECONDAIRES = 'images_secondaires',
  PAGE_TITRE = 'page_titre',

  // Advanced tabs
  ENREGISTREMENTS_LIES = 'enregistrements_lies', // Complet only
  MISE_EN_PAGE = 'mise_en_page', // Complet, Classeur

  // List Text specific tab
  OPTIONS_LISTE_TEXTE = 'options_liste_texte', // List Text only
}

export interface TabConfig {
  id: PrintTab;
  label: string;
  icon?: string;
  order: number;
  badge?: boolean; // Whether to show badge with selected count
}

export interface ModelTabConfiguration {
  model: PrintModel;
  modelLabel: string;
  tabs: TabConfig[];
}

/**
 * Tab Configuration per Model
 * Based on Figma mockup analysis
 */
export const PRINT_MODEL_TAB_CONFIG: Record<PrintModel, ModelTabConfiguration> = {
  /**
   * MODÈLE SIMPLE (Simple Sheet)
   * Purpose: Quick print with essential fields
   * Use case: Basic artwork documentation
   */
  [PrintModel.SIMPLE]: {
    model: PrintModel.SIMPLE,
    get modelLabel() {
      return visibleT("bo.print.modelSimple", "Fiche simple");
    },
    tabs: [
      {
        id: PrintTab.OPTIONS_AFFICHAGE,
        get label() {
          return visibleT("bo.print.tabDisplayOptions", "Options d'affichage");
        },
        order: 1,
        badge: true,
      },
      {
        id: PrintTab.IMAGES_SECONDAIRES,
        get label() {
          return visibleT("bo.print.tabSecondaryImages", "Images secondaires");
        },
        order: 2,
        badge: true,
      },
      {
        id: PrintTab.MISE_EN_PAGE,
        get label() {
          return visibleT("bo.print.tabPageLayout", "Mise en page");
        },
        order: 3,
        badge: false,
      },
      {
        id: PrintTab.PAGE_TITRE,
        get label() {
          return visibleT("bo.print.tabCoverPage", "Page titre");
        },
        order: 4,
        badge: false,
      },
      {
        id: PrintTab.PREVISUALISATION,
        get label() {
          return visibleT("bo.print.tabPreview", "Prévisualisation");
        },
        order: 5,
        badge: false,
      },
    ],
  },

  /**
   * MODÈLE COMPLET (Complete Sheet)
   * Purpose: Comprehensive print with all available fields
   * Use case: Detailed artwork documentation, archive records
   */
  [PrintModel.COMPLET]: {
    model: PrintModel.COMPLET,
    get modelLabel() {
      return visibleT("bo.print.modelComplete", "Fiche complète");
    },
    tabs: [
      {
        id: PrintTab.OPTIONS_AFFICHAGE,
        get label() {
          return visibleT("bo.print.tabDisplayOptions", "Options d'affichage");
        },
        order: 1,
        badge: true,
      },
      {
        id: PrintTab.ENREGISTREMENTS_LIES,
        get label() {
          return visibleT("bo.print.tabRelatedRecords", "Enregistrements liés");
        },
        order: 2,
        badge: true,
      },
      {
        id: PrintTab.IMAGES_SECONDAIRES,
        get label() {
          return visibleT("bo.print.tabSecondaryImages", "Images secondaires");
        },
        order: 3,
        badge: true,
      },
      {
        id: PrintTab.PAGE_TITRE,
        get label() {
          return visibleT("bo.print.tabCoverPage", "Page titre");
        },
        order: 4,
        badge: false,
      },
      {
        id: PrintTab.MISE_EN_PAGE,
        get label() {
          return visibleT("bo.print.tabPageLayout", "Mise en page");
        },
        order: 5,
        badge: false,
      },
      {
        id: PrintTab.PREVISUALISATION,
        get label() {
          return visibleT("bo.print.tabPreview", "Prévisualisation");
        },
        order: 6,
        badge: false,
      },
    ],
  },

  /**
   * MODÈLE CLASSEUR (Binder Sheet / "Grande Photo")
   * Purpose: Compact format for binder/folder organization
   * Use case: Physical filing, reduced footprint printing
   *
   * Note : la page de couverture est désormais autorisée (retour client) —
   * l'ancienne règle RG-2.5 (INVZ-013) qui l'interdisait a été levée.
   */
  [PrintModel.CLASSEUR]: {
    model: PrintModel.CLASSEUR,
    get modelLabel() {
      return visibleT("bo.print.modelBinder", "Fiche classeur");
    },
    tabs: [
      {
        id: PrintTab.OPTIONS_AFFICHAGE,
        get label() {
          return visibleT("bo.print.tabDisplayOptions", "Options d'affichage");
        },
        order: 1,
        badge: true,
      },
      {
        id: PrintTab.MISE_EN_PAGE,
        get label() {
          return visibleT("bo.print.tabPageLayout", "Mise en page");
        },
        order: 2,
        badge: false,
      },
      {
        id: PrintTab.PAGE_TITRE,
        get label() {
          return visibleT("bo.print.tabCoverPage", "Page titre");
        },
        order: 3,
        badge: false,
      },
      {
        id: PrintTab.PREVISUALISATION,
        get label() {
          return visibleT("bo.print.tabPreview", "Prévisualisation");
        },
        order: 4,
        badge: false,
      },
    ],
  },

  /**
   * MODÈLE LISTE TEXTE (Text List)
   * Purpose: Quick inventory or artwork list in summary mode
   * Use case: Generate a concise text-based list with optional thumbnails
   */
  [PrintModel.LIST_TEXT]: {
    model: PrintModel.LIST_TEXT,
    get modelLabel() {
      return visibleT("bo.print.modelListText", "Liste Texte");
    },
    tabs: [
      {
        id: PrintTab.MISE_EN_PAGE,
        get label() {
          return visibleT("bo.print.tabPageLayout", "Mise en page");
        },
        order: 1,
        badge: false,
      },
      {
        id: PrintTab.PREVISUALISATION,
        get label() {
          return visibleT("bo.print.tabPreview", "Prévisualisation");
        },
        order: 2,
        badge: false,
      },
    ],
  },
};

/**
 * Get tab configuration for a specific print model
 */
export function getTabsForModel(model: PrintModel): TabConfig[] {
  return PRINT_MODEL_TAB_CONFIG[model]?.tabs ?? [];
}

/**
 * Check if a specific tab is available for a given model
 */
export function isTabAvailableForModel(model: PrintModel, tabId: PrintTab): boolean {
  const tabs = getTabsForModel(model);
  return tabs.some(tab => tab.id === tabId);
}

/**
 * Get all available models for dropdown
 */
export function getAvailableModels(): { value: PrintModel; label: string }[] {
  return Object.values(PRINT_MODEL_TAB_CONFIG).map(config => ({
    value: config.model,
    label: config.modelLabel,
  }));
}

/**
 * Default model selection
 */
export const DEFAULT_PRINT_MODEL = PrintModel.SIMPLE;
